"use client";

import { Slot } from "@radix-ui/react-slot";
import { cn } from "@repo/helpers";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import * as React from "react";

import { Button, ButtonTypes } from "../../button";
import style from "./style.module.scss";

function AlertDialog({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
	return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
	return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
	return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
	return (
		<AlertDialogPrimitive.Overlay
			data-slot="alert-dialog-overlay"
			className={cn(style.overlay, className)}
			{...props}
		/>
	);
}

function AlertDialogContent({
	className,
	size = "default",
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & {
	size?: "default" | "sm" | "lg" | "auto";
}) {
	return (
		<AlertDialogPortal>
			<AlertDialogOverlay />
			<AlertDialogPrimitive.Content
				data-slot="alert-dialog-content"
				data-size={size}
				className={cn(style.content, className)}
				{...props}
			/>
		</AlertDialogPortal>
	);
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
	return <div data-slot="alert-dialog-header" className={cn(style.header, className)} {...props} />;
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
	return <div data-slot="alert-dialog-footer" className={cn(style.footer, className)} {...props} />;
}

function AlertDialogMedia({ className, ...props }: React.ComponentProps<"div">) {
	return <div data-slot="alert-dialog-media" className={cn(style.media, className)} {...props} />;
}

function AlertDialogTitle({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
	return (
		<AlertDialogPrimitive.Title data-slot="alert-dialog-title" className={cn(style.title, className)} {...props} />
	);
}

function AlertDialogDescription({
	className,
	asChild = false,
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description> & {
	asChild?: boolean;
}) {
	const Comp = asChild ? Slot : AlertDialogPrimitive.Description;
	return (
		<Comp data-slot="alert-dialog-description" className={cn(style.description, className)} {...(props as any)} />
	);
}

function AlertDialogAction({
	color = "darkBlue",
	size = "medium",
	types = [ButtonTypes.Primary],
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> &
	Pick<React.ComponentProps<typeof Button>, "color" | "size" | "types">) {
	const { children, style: _style, ...rest } = props;

	return (
		<Button color={color} size={size} types={types} style={_style as React.CSSProperties} {...rest}>
			{children}
		</Button>
	);
}

function AlertDialogCancel({
	color = "red",
	size = "medium",
	types = [ButtonTypes.Outline],
	...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> &
	Pick<React.ComponentProps<typeof Button>, "color" | "size" | "types">) {
	const { children, style: _style, ...rest } = props;

	return (
		<Button size={size} types={types} color={color} style={_style as React.CSSProperties} {...rest}>
			{children}
		</Button>
	);
}

export {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogOverlay,
	AlertDialogPortal,
	AlertDialogTitle,
	AlertDialogTrigger,
};
